import { utils_mixin } from '@old/mixins/utils_mixin'
import { api_mixin } from '@old/mixins/api_mixin'
import { mapActions, mapState } from 'vuex'

export const actions_mixin = {
    mixins: [api_mixin, utils_mixin],
    data() {
        return {
            resolve_profile_dialog_promise_request_invoice_copy: () => null,
            reject_profile_dialog_promise_request_invoice_copy: () => null,
        }
    },
    watch: {
        profile_dialog: {
            handler(value) {
                const mobile_number = this.deep_get(this.user, 'mobile_number_data.mobile_number', '')
                if (!value) {
                    if (mobile_number) {
                        this.resolve_profile_dialog_promise_request_invoice_copy()
                    } else {
                        this.reject_profile_dialog_promise_request_invoice_copy()
                    }
                }
            },
        },
    },
    computed: {
        ...mapState([
            'actions_render_key',
            'direct_debit_payments',
            'amortization',
            'respite',
            'user',
            'profile_dialog',
            'message_dialog',
        ]),
    },
    methods: {
        demand_letter_available(document) {
            return this.deep_get(document, 'print.bucket_id') && this.deep_get(document, 'print.print_id')
        },
        async demand_letter_action(document) {
            await this.set_state_property({
                property: 'media',
                data: {
                    resource: 'prints',
                    bucket_id: this.deep_get(document, 'print.bucket_id'),
                    document_id: this.deep_get(document, 'print.print_id'),
                    content_type: this.deep_get(document, 'print_document.print.content_type'),
                },
            })
        },
        original_invoice_available(document) {
            return document.reference === 'case' && this.deep_get(document, 'original_invoice.id')
        },
        async original_invoice_action(document) {
            await this.set_state_property({
                property: 'media',
                data: {
                    resource: 'cases',
                    bucket_id: this.deep_get(document, 'original_invoice.id'),
                    content_type: this.deep_get(document, 'original_invoice.content_type'),
                    document_id: document['_id'],
                },
            })
        },
        async request_invoice_copy_available(document) {
            if (this.original_invoice_available(document)) return false

            const ref = document.reference
            if (!['case', 'debt_collection'].includes(ref)) {
                return false
            }

            const res = {
                case: 'cases',
                debt_collection: 'case--debt-collections',
            }[ref]

            const response = await this.api_get({
                url: `/domain-actions/${res}/request_invoice_copy/${document['_id']}`,
            })
            return Boolean(response.status === 200)
        },
        async request_invoice_copy_action(document) {
            try {
                const mobile_number = this.deep_get(this.user, 'mobile_number_data.mobile_number', '')

                if (!mobile_number) {
                    await new Promise((resolve, reject) => {
                        this.resolve_profile_dialog_promise_request_invoice_copy = resolve
                        this.reject_profile_dialog_promise_request_invoice_copy = reject
                        this.set_state_property({
                            property: 'profile_dialog',
                            data: {
                                context: 'request_invoice_copy',
                            },
                        })
                    })
                }

                const ref = document.reference
                const res = {
                    case: 'cases',
                    debt_collection: 'case--debt-collections',
                }[ref]

                await this.api_post({
                    url: `/domain-actions/${res}/request_invoice_copy/${document['_id']}`,
                    data: {},
                })

                await this.set_state_property({
                    property: 'message_dialog',
                    data: {
                        title: 'common.invoice_copy_request_success_title',
                        text: 'common.invoice_copy_request_success_text',
                    },
                })
            } catch (e) {
                console.error(e)
            }
        },
        async respite_available(document) {
            if (document.reference !== 'case' && document.reference !== 'debt_collection') {
                await this.set_action_error('respite', 'common.respite_error_status')
                return false
            }
            if (!this.unpaid(document)) {
                await this.set_action_error('respite', 'common.respite_error_general')
                return false
            }

            const id = document['cases'] ? document['cases'][0]['case'] : document['_id']
            const response = await this.api_get({
                url: `/domain-actions/cases/respite/${id}`,
            })
            if (response.status === 200) {
                await this.update_key_in_state_property({
                    property: 'respite',
                    key: 'params',
                    data: {
                        ...response.data.params,
                        schedule_to_execute: response.data.schedule_to_execute,
                        id: id,
                    },
                })
                return true
            } else if (response.status === 206) {
                const issues = response.data['_issues']
                if (issues.some((v) => v.includes('status'))) {
                    await this.set_action_error('respite', 'common.respite_error_status')
                } else if (issues.some((v) => v.includes('max_number_respite'))) {
                    await this.set_action_error('respite', 'common.respite_error_max')
                } else {
                    await this.set_action_error('respite', 'common.respite_error_general')
                }
                return false
            }
        },
        async respite_action(document) {
            await this.update_key_in_state_property({
                property: 'respite',
                key: 'document',
                data: document,
            })
        },
        async amortization_available(document) {
            if (document.reference !== 'case' && document.reference !== 'debt_collection') {
                await this.set_action_error('amortization', 'common.amortization_error_status')
                return false
            }
            if (!this.unpaid(document) || this.active_direct_debit_payment(document)) {
                await this.set_action_error('amortization', 'common.amortization_error_general')
                return false
            }

            const id = document['cases'] ? document['cases'][0]['case'] : document['_id']
            const response = await this.api_get({
                url: `/domain-actions/cases/new_amortization/${id}`,
            })
            if (response.status === 200) {
                await this.update_key_in_state_property({
                    property: 'amortization',
                    key: 'params',
                    data: {
                        ...response.data,
                        id: id,
                    },
                })
                return true
            } else if (response.status === 206) {
                const issues = response.data['_issues']
                if (issues.some((v) => v.includes('status'))) {
                    await this.set_action_error('amortization', 'common.amortization_error_status')
                } else if (issues.some((v) => v.includes('max_number_of_active'))) {
                    await this.set_action_error('amortization', 'common.amortization_error_max_active')
                } else {
                    await this.set_action_error('amortization', 'common.amortization_error_general')
                }
                return false
            }
        },
        async amortization_action(document) {
            await this.update_key_in_state_property({
                property: 'amortization',
                key: 'document',
                data: document,
            })
        },
        pay_directly_available(document) {
            return this.unpaid(document) && !this.active_direct_debit_payment(document)
        },
        async pay_directly_action(document) {
            await this.set_state_property({
                property: 'payment',
                data: document,
            })
        },
        async set_action_error(key, error) {
            await this.update_key_in_state_property({
                property: key,
                key: 'error',
                data: error,
            })
        },
        async update_actions_render_key() {
            const new_value = this.actions_render_key + 1
            await this.set_state_property({
                property: 'actions_render_key',
                data: new_value,
            })
        },
        unpaid(document) {
            return this.deep_get(document, 'ocr.status') === 'unpaid'
        },
        active_direct_debit_payment(document) {
            const direct_debit_payment = this.direct_debit_payments[document._id]
            if (direct_debit_payment) {
                return ['pending', 'retry'].includes(direct_debit_payment.status)
            }
            return false
        },
        get_action_tooltip(key) {
            if (key === 'payment') {
                return this.translate('common.payment_error_general')
            } else if (this[key]) {
                return this.translate(this[key].error) || this.$t('common.unavailable')
            }
            return null
        },
        ...mapActions(['set_state_property', 'update_key_in_state_property']),
    },
}
